import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { HEARTBEAT_MS, heartbeat, sessionTenant, startSession, watchSession } from "../services/sessions";
import { getSessionPolicy } from "../services/sessionPolicy";
import { setSignOutReason } from "../services/sessionTimeout";
import { logError } from "../services/logger";

/**
 * Registers this tab in `sessions` (AC-02, AC-08): refuses the sign-in when
 * the company already has its policy's session limit of users online, keeps a heartbeat, and
 * signs out when a manager revokes the session. The shared demo account is
 * not counted.
 */
export default function SessionRegistry() {
  const navigate = useNavigate();
  const { user, userProfile, logout, isDemoUser } = useAuth();
  const uid = user?.uid;
  const ready = !!uid && !!userProfile && !isDemoUser();

  useEffect(() => {
    if (!ready) return undefined;
    let cancelled = false;
    let timer = null;
    let unwatch = null;

    const signOutWith = async (reason) => {
      setSignOutReason(reason);
      await logout().catch(() => {});
      navigate("/auth/login", { replace: true });
    };

    (async () => {
      try {
        const { sessionLimit } = await getSessionPolicy(sessionTenant(userProfile, uid));
        if (cancelled) return;
        const id = await startSession({ uid, profile: userProfile, limit: sessionLimit });
        if (cancelled) return;
        timer = setInterval(() => {heartbeat(id).catch(() => {});}, HEARTBEAT_MS);
        unwatch = watchSession(id, () => signOutWith("revoked"));
      } catch (err) {
        if (cancelled) return;
        if (err?.code === "SESSION_LIMIT") await signOutWith("session_limit");else
        logError("SessionRegistry", err);
      }
    })();

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      if (unwatch) unwatch();
    };
    // Re-register only when the signed-in account changes, not on every profile refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, uid]);

  return null;
}
